import React, { useState, useEffect, useRef } from 'react';
import './Photos.css';

// LazyImage component with intersection observer
function LazyImage({ src, alt, className }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} style={{ position: 'relative', minHeight: '100px' }}>
      {isInView ? (
        <img
          src={src}
          alt={alt}
          className={className}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
          onLoad={() => setIsLoaded(true)}
        />
      ) : (
        <div style={{
          backgroundColor: '#f5f5f5',
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ccc',
          fontSize: '14px'
        }}>
          ⬇
        </div>
      )}
    </div>
  );
}

function Photos({ onGoHome }) {
  const [photos, setPhotos] = useState({});
  const [viewMode, setViewMode] = useState('line');
  
  // Handle escape key for going home
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onGoHome();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onGoHome]);

  useEffect(() => {
    const photoLocations = {
      'mongolia-2025': [
        '/assets/images/mongolia-2025/000035210004.png',
        '/assets/images/mongolia-2025/000035210005.jpg',
        '/assets/images/mongolia-2025/000035210006.jpg',
        '/assets/images/mongolia-2025/000035210007.jpg',
        '/assets/images/mongolia-2025/000035210008.jpg',
        '/assets/images/mongolia-2025/000035210009.jpg',
        '/assets/images/mongolia-2025/000035210013.jpg',
        '/assets/images/mongolia-2025/000035210017.jpg',
        '/assets/images/mongolia-2025/000035210025.jpg',
        '/assets/images/mongolia-2025/000035210027.jpg',
        '/assets/images/mongolia-2025/000035210029.jpg',
        '/assets/images/mongolia-2025/16AAE924-AFB6-4E83-BBF8-9C11AB804DDF.jpg',
        '/assets/images/mongolia-2025/5F4B1371-CCD8-40B2-B473-40190EF6E2AF.jpg',
        '/assets/images/mongolia-2025/B0321D09-57D7-41D9-84DF-95EC885222C7.jpg',
        '/assets/images/mongolia-2025/D13C9B8F-C559-4737-89B7-4EF5EAD04473.jpg',
        '/assets/images/mongolia-2025/D21A4D98-EB7F-4C9C-888E-AAF65ECDDB24.jpg',
        '/assets/images/mongolia-2025/E01B5729-0F93-4631-A2D0-4EAA72B138F9.jpg'
      ],
      'greece-2025': [
        '/assets/images/greece-2025/000054.jpeg',
        '/assets/images/greece-2025/000057.jpeg',
        '/assets/images/greece-2025/000058.jpeg',
        '/assets/images/greece-2025/000059.jpeg',
        '/assets/images/greece-2025/EA746B75-AF93-455E-A9D2-6E4156C6E77C.jpg',
        '/assets/images/greece-2025/IMG_1443.JPG',
        '/assets/images/greece-2025/IMG_1764.png'
      ],
      'bangkok-2025': [
        '/assets/images/bangkok-2025/000001.jpeg',
        '/assets/images/bangkok-2025/000001%202.jpeg',
        '/assets/images/bangkok-2025/000002.jpeg',
        '/assets/images/bangkok-2025/000003.jpeg',
        '/assets/images/bangkok-2025/000047.jpeg',
        '/assets/images/bangkok-2025/000050.jpeg',
        '/assets/images/bangkok-2025/23BD5198-E7B5-47F7-9F3A-8645225EC2D7.jpg',
        '/assets/images/bangkok-2025/000042300003.jpg',
        '/assets/images/bangkok-2025/000042300004.jpg',
        '/assets/images/bangkok-2025/000042300005.jpg',
        '/assets/images/bangkok-2025/000042300006.jpg',
        '/assets/images/bangkok-2025/000042300007.jpg',
        '/assets/images/bangkok-2025/000042300008.jpg',
        '/assets/images/bangkok-2025/000042300009.jpg',
        '/assets/images/bangkok-2025/000042300010.jpg',
        '/assets/images/bangkok-2025/000042300012.jpg',
        '/assets/images/bangkok-2025/000042300013.jpg',
        '/assets/images/bangkok-2025/000042300014.jpg',
        '/assets/images/bangkok-2025/000042300016.jpg',
        '/assets/images/bangkok-2025/000042300019.jpg',
        '/assets/images/bangkok-2025/000042300022.jpg',
        '/assets/images/bangkok-2025/000042300023.jpg',
        '/assets/images/bangkok-2025/000042300024.jpg',
        '/assets/images/bangkok-2025/000042300025.jpg',
        '/assets/images/bangkok-2025/000042300026.jpg'
      ],
      'paris-2025': [
        '/assets/images/paris-2025/IMG_9358.JPG',
        '/assets/images/paris-2025/IMG_9363.JPG',
        '/assets/images/paris-2025/IMG_9368.JPG',
        '/assets/images/paris-2025/IMG_9370.JPG',
        '/assets/images/paris-2025/IMG_9401.JPG',
        '/assets/images/paris-2025/IMG_9402.JPG',
        '/assets/images/paris-2025/IMG_9656.JPG',
        '/assets/images/paris-2025/IMG_9658.JPG',
        '/assets/images/paris-2025/IMG_9661.JPG',
        '/assets/images/paris-2025/IMG_9663.JPG',
        '/assets/images/paris-2025/IMG_9762.JPG',
        '/assets/images/paris-2025/Photo01_00A.jpg',
        '/assets/images/paris-2025/Photo11_9A.jpg',
        '/assets/images/paris-2025/Photo13_11A.jpg'
      ],
      'prague-2025': [
        '/assets/images/prague-2025/IMG_9672.JPG',
        '/assets/images/prague-2025/IMG_9674.JPG',
        '/assets/images/prague-2025/IMG_9675.JPG',
        '/assets/images/prague-2025/IMG_9678.JPG',
        '/assets/images/prague-2025/IMG_9679.JPG',
        '/assets/images/prague-2025/IMG_9687.JPG',
        '/assets/images/prague-2025/Photo07_5A.jpg'
      ],
      'taghazout-2025': [
        '/assets/images/taghazout-2025/Photo02_5.jpg',
        '/assets/images/taghazout-2025/Photo07_11.jpg',
        '/assets/images/taghazout-2025/Photo13_17.jpg',
        '/assets/images/taghazout-2025/Photo15_19.jpg',
        '/assets/images/taghazout-2025/Photo16_20.jpg',
        '/assets/images/taghazout-2025/Photo25_29.jpg',
        '/assets/images/taghazout-2025/Photo26_30.jpg',
        '/assets/images/taghazout-2025/Photo31_35.jpg',
        '/assets/images/taghazout-2025/Photo33_37.jpg'
      ],
      'toronto-2024': [
        '/assets/images/toronto-2024/61910CA5-65CB-4239-8304-D2954879EA0A_1_102_o.jpeg',
        '/assets/images/toronto-2024/852AB9AB-205E-4D50-8DA9-227A864E2163_1_102_o.jpeg',
        '/assets/images/toronto-2024/9428AE7A-6F9D-4621-A15B-2F3E43D16196_1_102_a.jpeg'
      ],
      'nyc-2024': [
        '/assets/images/nyc-2024/8B8FFB17-76B5-4D00-A45A-5FE37BF3F4AD_1_102_o.jpeg',
        '/assets/images/nyc-2024/926CE261-4CC0-41EE-8267-A712F4F46B09_1_102_a.jpeg',
        '/assets/images/nyc-2024/968BFEB7-D421-437B-B3F5-4CB72F70FA2E_1_102_a.jpeg',
        '/assets/images/nyc-2024/C0254B32-B231-44BE-AC79-FA2CDF3BEA55_1_102_a.jpeg',
        '/assets/images/nyc-2024/C4B85CD9-404F-42A7-B725-2198B1D93A42_1_102_o.jpeg',
        '/assets/images/nyc-2024/E5303AAE-11D6-40E2-8487-9DBA5FE446D6_1_102_a.jpeg',
        '/assets/images/nyc-2024/EEDC9DAB-5614-4344-BA22-909BE67E5738_1_102_a.jpeg',
        '/assets/images/nyc-2024/F342AC5C-9414-439F-AA01-02B462203958_1_102_a.jpeg',
        '/assets/images/nyc-2024/IMG_9387.JPG'
      ]
    };
    
    setPhotos(photoLocations);
  }, []);

  return (
    <div className="photo-gallery">
      <div className="gallery-header">
        <h1>Photography</h1>
        <button onClick={onGoHome} className="nav-button">Back</button>
        <button 
          onClick={() => setViewMode(viewMode === 'line' ? 'masonry' : 'line')}
          className="nav-button"
        >
          {viewMode === 'line' ? 'Tile View' : 'Line View'}
        </button>
      </div>
      
      <div className="gallery-content">
        {viewMode === 'line' ? (
          // Line view with location sections
          Object.keys(photos)
            .sort((a, b) => {
              if (a.includes('nyc')) return 1;
              if (b.includes('nyc')) return -1;
              return a.localeCompare(b);
            })
            .map(location => (
              <div key={location} className="location-section">
                <h2 className="location-title">{location}</h2>
                <div className="line-view">
                  {photos[location].map((photo, index) => (
                    <div key={`${location}-${index}`} className="line-photo">
                      <LazyImage
                        src={photo}
                        alt={`Photo ${index + 1} from ${location.replace('-', ' ')}`}
                        className="photo-img"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))
        ) : (
          // Pinterest masonry view - single unified grid
          <div className="masonry-container">
            <div className="masonry-column">
              {Object.keys(photos)
                .sort((a, b) => {
                  if (a.includes('nyc')) return 1;
                  if (b.includes('nyc')) return -1;
                  return a.localeCompare(b);
                })
                .flatMap(location => 
                  photos[location].map((photo, index) => ({ photo, location, index }))
                )
                .filter((_, i) => i % 3 === 0)
                .map(({ photo, location, index }, i) => (
                  <div key={`col1-${i}`} className="masonry-item">
                    <LazyImage
                      src={photo}
                      alt={`Photo ${index + 1} from ${location.replace('-', ' ')}`}
                      className="masonry-img"
                    />
                  </div>
                ))
            }
            </div>
            <div className="masonry-column">
              {Object.keys(photos)
                .sort((a, b) => {
                  if (a.includes('nyc')) return 1;
                  if (b.includes('nyc')) return -1;
                  return a.localeCompare(b);
                })
                .flatMap(location => 
                  photos[location].map((photo, index) => ({ photo, location, index }))
                )
                .filter((_, i) => i % 3 === 1)
                .map(({ photo, location, index }, i) => (
                  <div key={`col2-${i}`} className="masonry-item">
                    <LazyImage
                      src={photo}
                      alt={`Photo ${index + 1} from ${location.replace('-', ' ')}`}
                      className="masonry-img"
                    />
                  </div>
                ))
            }
            </div>
            <div className="masonry-column">
              {Object.keys(photos)
                .sort((a, b) => {
                  if (a.includes('nyc')) return 1;
                  if (b.includes('nyc')) return -1;
                  return a.localeCompare(b);
                })
                .flatMap(location => 
                  photos[location].map((photo, index) => ({ photo, location, index }))
                )
                .filter((_, i) => i % 3 === 2)
                .map(({ photo, location, index }, i) => (
                  <div key={`col3-${i}`} className="masonry-item">
                    <LazyImage
                      src={photo}
                      alt={`Photo ${index + 1} from ${location.replace('-', ' ')}`}
                      className="masonry-img"
                    />
                  </div>
                ))
            }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Photos;
